import { useCallback } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { Bookmark, ListChecks, Bell, UserPlus } from '@phosphor-icons/react';
import { useAuth } from '../hooks/useAuth';
import { trackEvent } from '../utils/analytics/analytics';
import { TOOLS_BTN_PRIMARY } from '../layouts/tools-page-classes';
import { Msg } from '../i18n-review/client/Msg';

const messages = defineMessages({
  title: {
    id: 'search.signInCta.title',
    defaultMessage: 'Never lose track of a job again',
    description: 'Title for the account sign-up call-to-action banner shown to anonymous users on search results',
  },
  benefit1: {
    id: 'search.signInCta.benefit1',
    defaultMessage: 'Save jobs to review later',
    description: 'First benefit listed in the account sign-up call-to-action banner',
  },
  benefit2: {
    id: 'search.signInCta.benefit2',
    defaultMessage: 'Track every application in one board',
    description: 'Second benefit listed in the account sign-up call-to-action banner',
  },
  benefit3: {
    id: 'search.signInCta.benefit3',
    defaultMessage: 'Get alerted the moment new roles match',
    description: 'Third benefit listed in the account sign-up call-to-action banner',
  },
  button: {
    id: 'search.signInCta.button',
    defaultMessage: 'Activate account',
    description: 'Primary button label for the account sign-up call-to-action banner',
  },
  secondaryText: {
    id: 'search.signInCta.secondaryText',
    defaultMessage: 'Already have an account? Sign in',
    description: 'Secondary link inviting an existing user to sign in from the account sign-up call-to-action banner',
  },
});

export type SignInProgressCtaProps = {
  className?: string;
};

export function SignInProgressCta({ className }: SignInProgressCtaProps) {
  const { isAuthenticated, signIn } = useAuth();
  const intl = useIntl();
  const loginHref = '/auth/login';

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>) => {
      event.preventDefault();
      trackEvent({
        name: 'auth_interaction',
        params: { action: 'sign_in_click', surface: 'srp_progress_cta', locale: intl.locale },
      });
      signIn();
    },
    [signIn, intl.locale]
  );

  if (isAuthenticated) {
    return null;
  }

  return (
    <div className={className}>
      <div className="flex flex-col gap-4 rounded-2xl border border-[#DDD5C8] dark:border-[#4A3520] bg-jc-surface p-4 shadow-sm dark:shadow-none sm:p-6 lg:flex-row lg:items-center lg:justify-between lg:gap-6">
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-jc-page text-jc-cta dark:text-[#98CFA8]">
              <UserPlus className="h-4 w-4" />
            </div>
            <h2 className="text-base font-semibold text-jc-ink sm:text-lg">
              <Msg {...messages.title} />
            </h2>
          </div>

          <ul className="mt-3 flex flex-col gap-1.5 sm:mt-4">
            <li className="flex items-center gap-2 text-sm text-jc-ink-secondary">
              <Bookmark className="h-4 w-4 shrink-0 text-jc-muted" />
              <Msg {...messages.benefit1} />
            </li>
            <li className="flex items-center gap-2 text-sm text-jc-ink-secondary">
              <ListChecks className="h-4 w-4 shrink-0 text-jc-muted" />
              <Msg {...messages.benefit2} />
            </li>
            <li className="flex items-center gap-2 text-sm text-jc-ink-secondary">
              <Bell className="h-4 w-4 shrink-0 text-jc-muted" />
              <Msg {...messages.benefit3} />
            </li>
          </ul>
        </div>

        <div className="flex w-full flex-col items-stretch gap-2 lg:w-auto lg:items-end">
          <a href={loginHref} rel="nofollow" onClick={handleClick} className={`${TOOLS_BTN_PRIMARY} text-center`}>
            <Msg {...messages.button} />
          </a>
          <a
            href={loginHref}
            rel="nofollow"
            onClick={handleClick}
            className="text-center text-xs text-jc-muted underline underline-offset-2 hover:text-jc-ink-secondary sm:text-sm"
          >
            <Msg {...messages.secondaryText} />
          </a>
        </div>
      </div>
    </div>
  );
}

export default SignInProgressCta;
